import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Users, TrendingUp, Sparkles, Briefcase, Wrench } from "lucide-react";

const fmt = (n) => `$${n.toLocaleString()}`;

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

function InHouseTC() {
  return (
    <div className="mt-4 flex items-start gap-2 bg-[#f0f0f5] border border-black/10 p-3">
      <Wrench className="text-[#121275] flex-shrink-0 mt-0.5" size={13} />
      <p className="text-xs text-black/80 leading-relaxed">
        Use our <strong className="text-[#121275]">in-house TC</strong> for{" "}
        <strong>$350 per unit</strong> (optional). Covers audit + compliance review.
      </p>
    </div>
  );
}

export default function FeeStructure() {
  return (
    <section
      id="fees"
      data-testid="fees-section"
      className="relative py-24 sm:py-32 lg:py-40 border-t border-black/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div {...fadeUp} transition={{ duration: 0.7 }} className="max-w-3xl mb-16">
          <div className="overline mb-4">Recruiting Fees</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#0a0a0a]">
            Transparent pricing.{" "}
            <em className="not-italic text-[#121275]">Every tier.</em>
          </h2>
          <p className="mt-6 text-black/60 max-w-2xl">
            New agent, high producer, team, or commercial — pricing is simple and posted
            publicly.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* NEW AGENTS */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6 }}
            data-testid="plan-new-agent"
            className="bg-[#f7f7f7] border border-black/10 p-6 lg:p-7 hover:border-[#121275]/40 transition-colors flex flex-col"
          >
            <Sparkles className="text-[#121275]" size={24} strokeWidth={1.25} />
            <div className="overline mt-4">Plan A</div>
            <h3 className="font-display text-2xl text-[#0a0a0a] font-light tracking-tight mt-2">
              New Real Estate Agents
            </h3>
            <p className="text-sm text-black/55 mt-3">
              Just licensed? Start here.
            </p>

            <div className="mt-6 bg-black/5 border border-black/10 p-5">
              <div className="numeric text-xs uppercase tracking-widest text-black/40 mb-2">
                Flat annual fee
              </div>
              <div className="flex items-baseline gap-2">
                <span className="numeric text-3xl text-[#121275]">{fmt(3500)}</span>
                <span className="numeric text-xs uppercase tracking-widest text-black/40">
                  / year
                </span>
              </div>
              <div
                className="h-px my-4"
                style={{ background: "linear-gradient(90deg, rgba(212,175,55,0.4), transparent)" }}
              />
              <div className="numeric text-xs uppercase tracking-widest text-black/40 mb-2">
                Per unit sold
              </div>
              <div className="flex items-baseline gap-2">
                <span className="numeric text-2xl text-[#0a0a0a]">+{fmt(150)}</span>
                <span className="numeric text-xs uppercase tracking-widest text-black/40">
                  / unit
                </span>
              </div>
            </div>

            <InHouseTC />

            <ul className="mt-6 space-y-3 border-t border-black/10 pt-6 flex-1">
              {[
                "Keep 100% of your commission",
                "Full tech stack — CRM, MLS, e-sign",
                "Bi-weekly Zoom training + replay library",
                "E&O insurance included",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-black/70">
                  <Check className="text-[#121275] flex-shrink-0 mt-0.5" size={14} />
                  {b}
                </li>
              ))}
            </ul>

            <Link
              to="/apply"
              data-testid="plan-new-agent-apply"
              className="btn-primary w-full mt-6 group !py-3 text-xs"
            >
              Apply
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>

          {/* HIGH PRODUCER */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.05 }}
            data-testid="plan-high-producer"
            className="bg-[#f7f7f7] border border-[#121275]/30 p-6 lg:p-7 tracing-border flex flex-col relative"
          >
            <span className="absolute top-4 right-4 numeric text-[10px] uppercase tracking-widest text-[#121275] border border-[#121275]/40 px-2 py-1">
              Popular
            </span>
            <TrendingUp className="text-[#121275]" size={24} strokeWidth={1.25} />
            <div className="overline mt-4">Plan B</div>
            <h3 className="font-display text-2xl text-[#0a0a0a] font-light tracking-tight mt-2">
              High Producer
            </h3>
            <p className="text-sm text-black/55 mt-3">
              Closes 10 or more deals per year.
            </p>

            <div className="mt-6 bg-black/5 border border-[#121275]/30 p-5">
              <div className="numeric text-xs uppercase tracking-widest text-black/40 mb-2">
                Flat annual fee
              </div>
              <div className="flex items-baseline gap-2">
                <span className="numeric text-3xl text-[#121275]">{fmt(3500)}</span>
                <span className="numeric text-xs uppercase tracking-widest text-black/40">
                  / year
                </span>
              </div>
              <div
                className="h-px my-4"
                style={{ background: "linear-gradient(90deg, rgba(212,175,55,0.4), transparent)" }}
              />
              <div className="numeric text-xs uppercase tracking-widest text-black/40 mb-2">
                Per unit sold
              </div>
              <div className="flex items-baseline gap-2">
                <span className="numeric text-2xl text-[#0a0a0a]">+{fmt(150)}</span>
                <span className="numeric text-xs uppercase tracking-widest text-black/40">
                  / unit
                </span>
              </div>
            </div>

            <InHouseTC />

            <ul className="mt-6 space-y-3 border-t border-black/10 pt-6 flex-1">
              {[
                "Keep 100% of your commission",
                "Full tech stack — CRM, MLS, e-sign, AI",
                "Bi-weekly Zoom training + replay library",
                "E&O insurance included",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-black/70">
                  <Check className="text-[#121275] flex-shrink-0 mt-0.5" size={14} />
                  {b}
                </li>
              ))}
            </ul>

            <Link
              to="/apply"
              data-testid="plan-high-producer-apply"
              className="btn-primary w-full mt-6 group !py-3 text-xs"
            >
              Apply
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>

          {/* TEAMS */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="plan-teams"
            className="bg-[#f7f7f7] border border-black/10 p-6 lg:p-7 hover:border-[#121275]/40 transition-colors flex flex-col"
          >
            <Users className="text-[#121275]" size={24} strokeWidth={1.25} />
            <div className="overline mt-4">Plan C</div>
            <h3 className="font-display text-2xl text-[#0a0a0a] font-light tracking-tight mt-2">
              Teams of 2+
            </h3>
            <p className="text-sm text-black/55 mt-3">
              For partnerships. Signed team agreement required.
            </p>

            <div className="mt-6 bg-black/5 border border-black/10 p-5">
              <div className="numeric text-xs uppercase tracking-widest text-black/40 mb-2">
                Base team fee / year
              </div>
              <div className="flex items-baseline gap-2">
                <span className="numeric text-3xl text-[#121275]">{fmt(5000)}</span>
                <span className="numeric text-xs uppercase tracking-widest text-black/40">
                  / first 2 members
                </span>
              </div>
              <div
                className="h-px my-4"
                style={{ background: "linear-gradient(90deg, rgba(212,175,55,0.4), transparent)" }}
              />
              <div className="numeric text-xs uppercase tracking-widest text-black/40 mb-2">
                Each additional teammate
              </div>
              <div className="flex items-baseline gap-2">
                <span className="numeric text-2xl text-[#0a0a0a]">+{fmt(500)}</span>
                <span className="numeric text-xs uppercase tracking-widest text-black/40">
                  / extra member
                </span>
              </div>
            </div>

            <InHouseTC />

            <ul className="mt-6 space-y-3 border-t border-black/10 pt-6 flex-1">
              {[
                "Everything above, per team member",
                "Team-branded marketing & lead routing",
                "Bi-weekly Zoom training + replay library",
                "E&O insurance included",
                "Signed team agreement per member",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-black/70">
                  <Check className="text-[#121275] flex-shrink-0 mt-0.5" size={14} />
                  {b}
                </li>
              ))}
            </ul>

            <Link
              to="/apply"
              data-testid="plan-teams-apply"
              className="btn-primary w-full mt-6 group !py-3 text-xs"
            >
              Apply
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>

          {/* COMMERCIAL */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.15 }}
            data-testid="plan-commercial"
            className="bg-[#f7f7f7] border border-black/10 p-6 lg:p-7 hover:border-[#121275]/40 transition-colors flex flex-col"
          >
            <Briefcase className="text-[#121275]" size={24} strokeWidth={1.25} />
            <div className="overline mt-4">Plan D</div>
            <h3 className="font-display text-2xl text-[#0a0a0a] font-light tracking-tight mt-2">
              Commercial Transactions
            </h3>
            <p className="text-sm text-black/55 mt-3">
              For commercial deals — different math.
            </p>

            <div className="mt-6 bg-black/5 border border-black/10 p-5">
              <div className="numeric text-xs uppercase tracking-widest text-black/40 mb-2">
                Commission split
              </div>
              <div className="flex items-baseline gap-2">
                <span className="numeric text-3xl text-[#121275]">90 / 10</span>
              </div>
              <div className="mt-1 numeric text-[11px] text-black/50">
                Agent keeps 90% · Brokerage 10%
              </div>
              <div
                className="h-px my-4"
                style={{ background: "linear-gradient(90deg, rgba(212,175,55,0.4), transparent)" }}
              />
              <div className="numeric text-xs uppercase tracking-widest text-black/40 mb-2">
                Plus audit fee
              </div>
              <div className="flex items-baseline gap-2">
                <span className="numeric text-2xl text-[#0a0a0a]">+{fmt(150)}</span>
                <span className="numeric text-xs uppercase tracking-widest text-black/40">
                  / transaction
                </span>
              </div>
            </div>

            <InHouseTC />

            <ul className="mt-6 space-y-3 border-t border-black/10 pt-6 flex-1">
              {[
                "90/10 commission split",
                "Audit fee per transaction",
                "Bi-weekly Zoom training + replay library",
                "E&O insurance included",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-black/70">
                  <Check className="text-[#121275] flex-shrink-0 mt-0.5" size={14} />
                  {b}
                </li>
              ))}
            </ul>

            <Link
              to="/apply"
              data-testid="plan-commercial-apply"
              className="btn-primary w-full mt-6 group !py-3 text-xs"
            >
              Apply
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
